import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import {
  LeoChat,
  LeoDock,
  type LeoLabels,
  type LeoMessage,
  type LeoStatus
} from "@/zapi-frontend/widgets/CreateProjectChat/ui/blocks/LeoChat";
import { SetupLinkDialog, type SetupLabels } from "@/zapi-frontend/widgets/CreateProjectChat/ui/blocks/ProjectSetup";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useLeoDockOpen } from "@/zapi-frontend/widgets/CreateProjectChat/models/useLeoDockOpen";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";

// The width from which Leo docks beside the page instead of opening over it.
const DOCK_QUERY = "(min-width: 64rem)";

type TCreateProjectLeoChatProps = {
  // The conversation's language, which the panel speaks too.
  t: TFunction<"createProject">;
  conversation: ReactNode;
  initializing: boolean;
  status: LeoStatus;
  // Leo's last message while it waits on the user, for the reminder on the closed launcher.
  waiting: LeoMessage | null;
  composerHidden: boolean;
  composerDisabled: boolean;
  // A new value puts the cursor in the composer, unless the user is typing somewhere on the page.
  focusComposerFor: string | null;
  placeholder: string;
  attachAccept: string;
  onSend: (text: string) => void;
  onAttach?: (files: File[]) => void;
  onAddLink?: () => void;
  linkDialog: { open: boolean; onOpenChange: (open: boolean) => void; onAdd: (url: string) => void };
  fileInput: ReactNode;
};

function leoLabels(t: TFunction<"createProject">): Partial<LeoLabels> {
  return {
    panel: t("leo.panel"),
    hide: t("leo.hide"),
    close: t("leo.close"),
    ask: t("leo.ask"),
    askWaiting: t("leo.askWaiting"),
    waiting: t("leo.waiting"),
    dismiss: t("leo.dismiss"),
    closeOverlay: t("leo.closeOverlay"),
    conversation: t("leo.conversation"),
    message: t("leo.message"),
    attach: t("leo.attach"),
    attachFile: t("newProject.attachFile"),
    files: t("leo.files"),
    webLink: t("leo.webLink"),
    send: t("leo.send"),
    disclaimer: t("leo.disclaimer"),
    thinking: t("leo.thinking"),
    done: t("leo.done"),
    scrollToEnd: t("leo.scrollToEnd"),
    more: (count: number) => t("leo.more", { count })
  };
}

function linkDialogLabels(t: TFunction<"createProject">): Partial<SetupLabels> {
  return {
    linkDialogTitle: t("newProject.linkTitle"),
    linkLabel: t("newProject.linkLabel"),
    linkPlaceholder: t("newProject.linkPlaceholder"),
    invalidLink: t("newProject.linkInvalid"),
    cancel: t("newProject.cancel"),
    addLink: t("newProject.addLink"),
    close: t("newProject.close")
  };
}

// The create-project conversation in Leo's panel: docked beside the page from 1024px, behind the
// launcher on narrower screens. Leo opens with the page; hiding it here is remembered for other pages.
export function CreateProjectLeoChat({
  t,
  conversation,
  initializing,
  status,
  waiting,
  composerHidden,
  composerDisabled,
  focusComposerFor,
  placeholder,
  attachAccept,
  onSend,
  onAttach,
  onAddLink,
  linkDialog,
  fileInput
}: TCreateProjectLeoChatProps) {
  const labels = useMemo(() => leoLabels(t), [t]);
  const linkLabels = useMemo(() => linkDialogLabels(t), [t]);
  const [openOnProjectList, rememberLeoOpen] = useLeoDockOpen();
  const [leoOpen, setLeoOpenHere] = useState(true);
  // Already open on the project list: it stays in place. Closed there: it opens as if the user had opened it.
  const [leoSlidesOpen] = useState(!openOnProjectList);
  const setLeoOpen = useCallback(
    (open: boolean) => {
      setLeoOpenHere(open);
      rememberLeoOpen(open);
    },
    [rememberLeoOpen]
  );
  const wide = useMediaQuery(DOCK_QUERY);

  useEffect(() => {
    if (!focusComposerFor) return;
    const active = document.activeElement;
    const editingPage =
      active instanceof HTMLElement &&
      active.matches("input, textarea, select, [contenteditable='true']") &&
      !active.closest("[data-slot=leo-panel]");
    if (editingPage) return;
    const composers = document.querySelectorAll<HTMLTextAreaElement>("[data-leo-composer]");
    Array.from(composers)
      .find((composer) => composer.offsetParent !== null)
      ?.focus();
  }, [focusComposerFor]);

  const content = initializing ? (
    <div className="flex h-full items-center justify-center">
      <Spinner aria-label={t("newProject.loading")} className="size-5 text-muted-foreground" />
    </div>
  ) : (
    conversation
  );
  const panel = {
    messages: waiting ? [waiting] : [],
    status,
    placeholder,
    attachAccept,
    composerDisabled: composerDisabled || initializing,
    composerHidden,
    labels,
    onSend,
    onAttach,
    onAddLink
  };

  return (
    <>
      <LeoDock
        {...panel}
        conversation={wide ? content : <div />}
        open={leoOpen}
        onOpenChange={setLeoOpen}
        appear={leoSlidesOpen}
      />
      <LeoChat
        {...panel}
        conversation={wide ? <div /> : content}
        docked={{ open: leoOpen, onOpenChange: setLeoOpen }}
      />
      <SetupLinkDialog
        open={linkDialog.open}
        onOpenChange={linkDialog.onOpenChange}
        onAdd={linkDialog.onAdd}
        labels={linkLabels}
      />
      {fileInput}
    </>
  );
}
